// src/pages/MagazineLayout.js
import React, { useRef, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import GlobalStyles from "styles/GlobalStyles";
import { magazines as staticMagazines } from "./magazinesData"; 
import { fetchCategories } from "./magazineApi"; 
import Layout from "components/layouts/Layout";
import WordSpacedText from "../components/WordSpacedText"; 

export default function MagazineLayout() {
  const navigate = useNavigate();

  const [search, setSearch] = useState("");
  const [magazines, setMagazines] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const cardRefs = useRef({});

  useEffect(() => {
    let mounted = true;
    async function loadMagazines() {
      setLoading(true);
      setError(null);
      try {
        const data = await fetchCategories();
        let mags = [];
        const baseStatic =
          staticMagazines && staticMagazines.length > 0 ? staticMagazines[0] : {};
        if (Array.isArray(data)) {
          mags = data.map((name, idx) => ({
            id: idx + 1,
            name: String(name ?? "Untitled"),
            icon: baseStatic.icon ?? "📘",
            color: baseStatic.color ?? "#ffffff",
            description: baseStatic.description ?? baseStatic.summary ?? "",
            details: baseStatic.details ?? "",
            _staticBase: baseStatic,
          }));
        } else {
          mags = staticMagazines.map((s, idx) => ({
            ...s,
            id: s.id ?? idx + 1,
          }));
        }
        if (mags.length === 0) {
          mags = staticMagazines.map((s, idx) => ({
            ...s,
            id: s.id ?? idx + 1,
          }));
        }
        if (mounted) setMagazines(mags);
      } catch (err) {
        console.error("Failed to fetch magazine names:", err);
        if (mounted) setError(err?.message ?? "Failed to load magazines");
      } finally {
        if (mounted) setLoading(false);
      }
    }
    loadMagazines();
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    cardRefs.current = magazines.reduce((acc, mag) => {
      acc[mag.id] = acc[mag.id] || React.createRef();
      return acc;
    }, {});
  }, [magazines]);

  const filteredMagazines = magazines.filter(
    (mag) =>
      mag &&
      typeof mag.name === "string" &&
      mag.name.toLowerCase().includes(search.toLowerCase())
  );

  const handleSidebarClick = (name) => {
    navigate(`/magazine/${encodeURIComponent(name)}`);
  };

  const handleCardClick = (mag) => {
    navigate(`/magazine/${encodeURIComponent(mag.name)}`);
  };

  return (
    <>
      <GlobalStyles />

      {/* ✅ Responsive CSS */}
      <style>
        {`
          @media (max-width: 768px) {
            .magazine-layout {
              flex-direction: column;
            }
            .magazine-sidebar {
              width: 100% !important;
              border-right: none !important;
              border-bottom: 1px solid #ececec;
              padding: 16px !important;
            }
            .magazine-sidebar input {
              width: 100% !important;
              margin-bottom: 16px !important;
            }
            .magazine-main {
              padding: 20px !important;
            }
            .magazine-card {
              flex-direction: column !important;
              align-items: flex-start !important;
              padding: 20px !important;
            }
            .magazine-card div:first-child {
              margin-right: 0 !important;
              margin-bottom: 12px;
            }
          }
        `}
      </style>

      <div
        className="magazine-layout"
        style={{
          display: "flex",
          minHeight: "100vh",
          background: "#f9fafb",
          fontFamily: "'CustomFont', sans-serif",
        }}
      >
        {/* Sidebar */}
        <aside
          className="magazine-sidebar"
          style={{
            width: 270,
            background: "#fff",
            borderRight: "1px solid #ececec",
            padding: "32px 0 0 0",
            boxSizing: "border-box",
            display: "flex",
            flexDirection: "column",
            alignItems: "stretch",
          }}
        >
          <input
            type="text"
            placeholder="Search magazines..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{
              width: "80%",
              margin: "0 auto 24px auto",
              padding: "10px 14px",
              borderRadius: 8,
              border: "1px solid #e5e7eb",
              fontSize: 15,
              outline: "none",
              background: "#f3f4f6",
              display: "block",
              fontFamily: "'CustomFont', sans-serif",
            }}
          />
          <div
            style={{
              fontWeight: 700,
              fontSize: 17,
              color: "#22223b",
              marginLeft: 36,
              marginBottom: 18,
            }}
          >
            <WordSpacedText>Our Magazines</WordSpacedText>
          </div>

          {loading && <div style={{ marginLeft: 36 }}>Loading… 🐣</div>}
          {error && <div style={{ marginLeft: 36, color: "red" }}>{error}</div>}

          <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
            {filteredMagazines.map((mag) => (
              <li
                key={mag.id}
                onClick={() => handleSidebarClick(mag.name)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  padding: "8px 36px",
                  fontWeight: 500,
                  color: "#22223b",
                  cursor: "pointer",
                  borderRadius: 6,
                  fontSize: 16,
                }}
              >
                <span style={{ fontSize: 22 }}>{mag.icon}</span>
                <span>{mag.name}</span>
              </li>
            ))}
          </ul>
        </aside>

        {/* Main content */}
        <main
          className="magazine-main"
          style={{
            flex: 1,
            padding: "40px 40px 40px 40px",
            background: "#f9fafb",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <h2
            style={{
              fontSize: 22,
              fontWeight: 700,
              margin: "0 0 24px 0",
              color: "#111827",
            }}
          >
            <WordSpacedText>Featured Magazines</WordSpacedText>
          </h2>
          <div style={{ display: "flex", flexDirection: "column", gap: 28 }}>
            {filteredMagazines.map((mag) => (
              <React.Fragment key={mag.id}>
                <div
                  ref={cardRefs.current[mag.id]}
                  onClick={() => handleCardClick(mag)}
                  className="magazine-card"
                  style={{
                    background: mag.color,
                    borderRadius: 14,
                    display: "flex",
                    alignItems: "center",
                    padding: "28px 32px",
                    boxShadow: "0 2px 8px 0 rgba(0,0,0,0.03)",
                    minHeight: 110,
                    cursor: "pointer",
                  }}
                >
                  <div style={{ fontSize: 40, marginRight: 22 }}>{mag.icon}</div>
                  <div>
                    <div
                      style={{
                        fontSize: 20,
                        fontWeight: 700,
                        color:
                          mag.id === 1
                            ? "#0e766e"
                            : mag.id === 2
                            ? "#2563eb"
                            : mag.id === 3
                            ? "#9333ea"
                            : "#22223b",
                      }}
                    >
                      {mag.name}
                    </div>
                    <div
                      style={{
                        color: "#374151",
                        margin: "6px 0 7px 0",
                        fontSize: 15,
                      }}
                    >
                      <WordSpacedText>{mag.description}</WordSpacedText>
                    </div>
                    <div
                      style={{
                        color: "#6b7280",
                        fontSize: 14,
                        fontWeight: 500,
                      }}
                    >
                      <WordSpacedText>{mag.details}</WordSpacedText>
                    </div>
                  </div>
                </div>
              </React.Fragment>
            ))}

            {!loading && filteredMagazines.length === 0 && (
              <div style={{ color: "#6b7280" }}>
                <WordSpacedText>No magazines found. Try a different search!</WordSpacedText>
              </div>
            )}
          </div>
        </main>
      </div>

      {/* ✅ شعبہ صحافت Section */}
      <div
        style={{
          background: "#fff",
          padding: "40px 30px",
          marginTop: "40px",
          borderTop: "1px solid #e5e7eb",
          fontFamily: "'CustomFont', sans-serif",
          direction: "rtl",
        }}
      >
        <h2
          style={{
            fontSize: 35,
            fontWeight: 1000,
            marginBottom: 20,
            color: "#C78A41",
            textAlign: "center",
          }}
        >
          شعبہ صحافت
        </h2>

        <p style={{ fontSize: 20, lineHeight: 2, color: "#374151" }}>
          مشرف عمومی : شیخ عبدالباری جامعی مدنی حفطہ اللہ <br />
          <strong>مجلہ الرشد</strong> – مدیر مسوٴل: ابوحمدان اشرف فیضی حفطہ اللہ <br />
          <strong>مجلہ حائطیہ و سالنامہ السلسبیل</strong> – مشرف: شیخ عبدالباری جامعی مدنی حفطہ اللہ <br />
          مدیر: شیخ صلاح الدین الجامعی حفطہ اللہ <br />
          نائب مدیر: شیخ نسیم نثار الجامعی حفطہ اللہ <br />
          مدیر اعزازی: شیخ وسیم قاضی جامعی مدنی حفطہ اللہ <br />
          معاون: شیخ حافظ جی۔ ریان جامعی حفطہ اللہ مدنی
        </p>

        <p style={{ fontSize: 20, lineHeight: 2, marginTop: 20 }}>
          دورِ حاضر میں صحافت کی اہمیت کسی سے پوشیدہ نہیں۔ یہ ایک ایسا میدان ہے جہاں الفاظ کی طاقت سے حق اور باطل کے درمیان معرکہ آرائی ہوتی ہے۔
          بدقسمتی سے موجودہ دور میں اعداء اسلام نے صحافت کو اپنے مفادات کے لیے استعمال کرتے ہوئے حق کو باطل اور باطل کو حق بنا کر پیش کرنے کا سلسلہ جاری رکھا ہوا ہے۔
          اسلام اور مسلمانوں کے خلاف شکوک و شبہات اور غلط فہمیاں پھیلائی جا رہی ہیں، جس کے اثرات ہر سطح پر محسوس کیے جا رہے ہیں۔
          ایسے وقت میں ضروری ہے کہ دینی مدارس کے طلبہ صحافت کے اس اہم میدان میں مہارت حاصل کریں تاکہ وہ اسلام کی صحیح تعلیمات کو عام کر سکیں اور باطل پروپیگنڈے کا ڈٹ کر مقابلہ کریں۔
        </p>

        <p style={{ fontSize: 20, lineHeight: 2, marginTop: 20 }}>
          انہی مقاصد کے پیش نظر جامعہ محمدیہ عربیہ میں شعبہ صحافت کا قیام عمل میں لایا گیا ہے۔ اس شعبے کا مقصد طلبہ کو صحافت کے اصول و مبادیات سے روشناس کرانا،
          انہیں دینی، علمی، اصلاحی، تحقیقی و تجزیاتی انداز میں لکھنے کے قابل بنانا، اور معیاری اسلامی صحافت کے میدان میں ان کی رہنمائی کرنا ہے۔
        </p>

        <h3 style={{ fontSize: 26, fontWeight: 700, marginTop: 30, color: "#C78A41" }}>
          شعبہ کی خصوصیات اور سرگرمیاں:
        </h3>

        <p style={{ fontSize: 20, lineHeight: 2 }}>
          1. مضامین کی تیاری اور پیشکش: شعبہ صحافت کے تحت طلبہ مختلف عناوین پر مضامین لکھتے ہیں۔ یہ مضامین ذمہ داران صحافت کی رہنمائی اور نظر ثانی کے بعد جامعہ کے حائطیہ بورڈ پر آویزاں کیے جاتے ہیں۔
          جامعہ میں دو اہم حائطيہ (وال بورڈ) اویزاں کیے جاتے ہیں: <br />
          • پندرہ روزہ حائطيہ (ہر 15 دن میں ایک بار) <br />
          • ہفتہ واری حائطيہ (ہر ہفتے ایک بار)
        </p>

        <p style={{ fontSize: 20, lineHeight: 2 }}>
          2. سالنامہ السلسبیل: جامعہ کا ایک اہم کارنامہ سالنامہ "السلسبیل" کی اشاعت ہے، جو طلبہ کی جانب سے لکھے گئے معیاری مضامین پر مشتمل ہوتا ہے۔
          یہ اردو، عربی، انگریزی، تلگو، اور کنڑا جیسی زبانوں میں تیار کیے جاتے ہیں۔
        </p>

        <p style={{ fontSize: 20, lineHeight: 2 }}>
          3. دو ماہی مجلہ الرشد: ایک شاندار پیشکش جو 2021 سے جاری ہے اور علمی و ادبی حلقوں میں مقبول ہے۔
          اس میں خصوصی شمارے، تحقیقی مواد، اور دینی و ادبی مضامین شامل ہیں۔
        </p>

        <p style={{ fontSize: 20, lineHeight: 2, marginTop: 20 }}>
          شعبہ صحافت کے مشرفِ اعلیٰ شیخ عبدالباری جامعی مدنی حفطہ اللہ کی قیادت میں یہ شعبہ مسلسل ترقی کی جانب گامزن ہے۔
          یہ اسلامی صحافت کے میدان میں ایک روشن چراغ ہے جو مستقبل میں باطل کے خلاف حق کی آواز کو بلند کرنے میں اہم کردار ادا کرے گا۔ (ان شاء اللہ)
        </p>
      </div>
    </>
  );
}
